import { useReferrerOptions } from '../../../../utils/options';
import { useMutateAppointmentOverflow } from '../../../../query/appointments';
import { useValidatePatientAgeWarningDialog } from '.././common/useValidatePatientAgeWarningDialog';
import { useCallback, useState } from 'react';
import { getReferrerIds } from '../../../../components/views-components/staff/appointments/AppointmentReferrersUtil';
import { BookAppointmentForm } from '../../../../components/views-components/staff/appointments/EditAppointmentData';
import { useDuplicateUserCheck } from '../../../../hooks/user/useDuplicateUserCheck';
import { UserData, UserFormValues } from '../../../../types/users';
import { useCreateUserMutation } from '../../../../query/users';
import { successToast } from '../../../../utils/toast';
import { useCommonTranslation } from '../../../../hooks/i18n/useCommonTranslation';
import { useSite } from '../../../../context/SiteContext';
import { usePatientRole } from '../../../../context/RoleContext';
import { useAllGPSurgeries } from '../../../../hooks/referrals/useAllGPSurgeries';
import { useGPSurgeriesOptions } from '../../../../hooks/referrals/useGPSurgeriesOptions';
import {
  combineDateAndTime,
  DATE_FNS_UK_DATE_FORMAT,
  formatDate,
  formatISO,
  formatSecondsOfDayIntoTime,
  getSecondsOfDay,
  isAfter,
  isBefore,
  isInPast,
  parseDate,
} from '../../../../utils/dateUtil';
import { Validate } from 'react-hook-form';
import { useMaxAndMinFutureDateForStaffBooking } from '../../../../hooks/patient/booking/useMaxAndMinFutureDateForBooking';
import { parseAndValidateCorrectTimeAndDivisibleBy5Minutes } from '../../../../utils/dateValidator';
import { useSiteIntAttributes } from '../../../../hooks/useAttribute';
import {
  STAFF_CLOSE_HOUR_IN_SECONDS,
  STAFF_OPEN_HOUR_IN_SECONDS,
} from '../../../../configs/siteAndTrustAttributes';
import { DATE_PICKER_VALUE_FORMAT } from '../../../../components/common/Forms/DatePickerInput.hooks';

export type OverflowAppointmentForm = BookAppointmentForm & {
  apptDate?: string;
  apptTime?: string;
};

export type OverflowAppointmentWithUser = {
  user: UserData;
  appointment: OverflowAppointmentForm;
};

export const useOverflowWithPatientValidation = ({
  closeForm,
}: {
  closeForm: () => void;
}) => {
  const { t } = useCommonTranslation();
  const { isLoading: isOverflowProcessing, mutate: appointmentOverflow } =
    useMutateAppointmentOverflow({
      onSuccess: () => {
        successToast(t('appointment-booked-success'));
        closeForm();
      },
    });

  const { showSitePatientAgeWarningIfNeeded } =
    useValidatePatientAgeWarningDialog();
  const overflowPatientWithAgeValidation = useCallback(
    ({ user, appointment }: OverflowAppointmentWithUser) => {
      showSitePatientAgeWarningIfNeeded({
        onSuccess: () => {
          let appt_time: string | undefined = undefined;
          if (appointment.apptDate && appointment.apptTime)
            appt_time = formatISO(
              combineDateAndTime(appointment.apptDate, appointment.apptTime),
            );
          return appointmentOverflow({
            ...appointment,
            ...getReferrerIds(appointment, user),
            client_id: user.id,
            appt_time,
          });
        },
        user: user,
      });
    },
    [showSitePatientAgeWarningIfNeeded, appointmentOverflow],
  );

  const [showApptTimeSelect, setShowApptTimeSelect] = useState(false);

  return {
    overflowPatientWithAgeValidation,
    isOverflowProcessing,
    showApptTimeSelect,
    setShowApptTimeSelect,
  };
};

export const useOverflowWithPatient = ({
  closeForm,
}: {
  closeForm: () => void;
}) => {
  const { isLoading: isReferrersLoading, referrerOptions } =
    useReferrerOptions();

  const { isLoading: isSurgeriesLoading, data: surgeriesResponse } =
    useAllGPSurgeries();

  const { isOverflowProcessing, ...overflowRest } =
    useOverflowWithPatientValidation({ closeForm });

  return {
    referrerOptions,
    surgeriesResponse,
    isOverflowProcessing,
    isLoading: isReferrersLoading || isOverflowProcessing || isSurgeriesLoading,
    ...overflowRest,
  };
};

export const useOverflowNoPatient = ({
  closeForm,
}: {
  closeForm: () => void;
}) => {
  const { isLoading: isReferrersLoading, referrerOptions } =
    useReferrerOptions();

  const { isLoading: isSurgeriesLoading, gpSurgeriesOptions: surgeryOptions } =
    useGPSurgeriesOptions({});

  const {
    isOverflowProcessing,
    overflowPatientWithAgeValidation,
    ...overflowRest
  } = useOverflowWithPatientValidation({ closeForm });

  const { t } = useCommonTranslation();
  const { activeSite } = useSite();
  const { id: patientRoleId } = usePatientRole();
  const { mutateAsync: createUserAsync, isLoading: isCreateUserLoading } =
    useCreateUserMutation({
      onSuccess: () => {
        successToast(t('patient-created'));
      },
    });

  const createUserAndOverflow = useCallback<
    (params: { appointment: BookAppointmentForm; user: UserFormValues }) => any
  >(
    async ({ appointment, user }) => {
      const savedUser = await createUserAsync({
        ...user,
        active_site_id: activeSite.id,
        role_id: patientRoleId,
      });
      await overflowPatientWithAgeValidation({
        user: savedUser.data,
        appointment: appointment,
      });
      closeForm();
    },
    [
      activeSite,
      overflowPatientWithAgeValidation,
      createUserAsync,
      patientRoleId,
      closeForm,
    ],
  );

  const {
    duplicateUsers,
    resolveAsDuplicate: duplicateResolveSelect,
    resolveAsNOTDuplicate: duplicateResolveCreateNew,
    actionWithDuplicateCheck: createAndOverflowPatientWithValidation,
  } = useDuplicateUserCheck<{
    user: UserFormValues;
    appointment: BookAppointmentForm;
  }>({
    onResolvedAsDuplicate: async (params) => {
      await overflowPatientWithAgeValidation(params);
    },
    onResolvedAsNOTDuplicate: (params) => {
      createUserAndOverflow(params);
    },
  });
  return {
    duplicateUsers,
    duplicateResolveSelect,
    duplicateResolveCreateNew,
    createAndOverflowPatientWithValidation,
    referrerOptions,
    surgeryOptions,
    isLoading:
      isReferrersLoading ||
      isOverflowProcessing ||
      isSurgeriesLoading ||
      isCreateUserLoading,
    ...overflowRest,
  };
};

export const useOverflowTimeSelectValidation = ({
  getApptDateValue,
}: {
  getApptDateValue: () => string | undefined;
}) => {
  const { t } = useCommonTranslation();
  const { minDate, maxDate } = useMaxAndMinFutureDateForStaffBooking();
  const [openHourInSeconds = 0, closeHourInSeconds = 0] = useSiteIntAttributes(
    STAFF_OPEN_HOUR_IN_SECONDS,
    STAFF_CLOSE_HOUR_IN_SECONDS,
  );

  const validateApptDate: Validate<string | undefined> = (value) => {
    if (!value) return t('must-not-empty') as string;

    const date = parseDate(value, DATE_PICKER_VALUE_FORMAT);
    if (isBefore(date, minDate)) {
      return t('must-not-be-before-date', {
        date: formatDate(minDate, DATE_FNS_UK_DATE_FORMAT),
      }) as string;
    }

    if (isAfter(date, maxDate)) {
      return t('must-not-be-after-date', {
        date: formatDate(maxDate, DATE_FNS_UK_DATE_FORMAT),
      }) as string;
    }
  };

  const validateApptTime: Validate<string | undefined> = (value) => {
    if (!value) return t('must-not-empty') as string;

    const apptDateValue = getApptDateValue();
    const referenceDate =
      apptDateValue === undefined
        ? new Date()
        : parseDate(apptDateValue, DATE_PICKER_VALUE_FORMAT);
    const { message, parsedDate } =
      parseAndValidateCorrectTimeAndDivisibleBy5Minutes({
        input: value,
        t,
        referenceDate,
      });

    if (message !== undefined) {
      return message;
    }

    if (apptDateValue !== undefined && isInPast(parsedDate)) {
      return t('must-not-be-in-past') as string;
    }

    const secondsOfDay = getSecondsOfDay(parsedDate);
    if (
      secondsOfDay < openHourInSeconds ||
      secondsOfDay >= closeHourInSeconds
    ) {
      return t('must-be-in-working-hours', {
        openHour: formatSecondsOfDayIntoTime(openHourInSeconds),
        closeHour: formatSecondsOfDayIntoTime(closeHourInSeconds),
      }) as string;
    }
  };

  return {
    minDate,
    maxDate,
    validateApptDate,
    validateApptTime,
  };
};
